import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Routes } from "../../root/routes";
import { HelpDialog } from "./HelpDialog";
import { useHistory } from "react-router";

const GA_NUDGE_COOKIE_NAME = "polaris_nudge_ga_reconnect_dialog";
const REMIND_ME_LATER_HOURS = 24;

export interface ReconnectGANudgeDialogProps {
  hasGAWebProperty: boolean /* Whether the organization has a GA Property ID configured. */;
  hasGAAuthorization:
    | boolean
    | null
    | undefined /* Whether the organization is actively connected to GA. */;
  organizationId: string /* The current organization ID */;
}

/**
 * An alert to nudge an org to reconnect their Google Analytics account.
 * The reminder cookie is tied to the specific organization, so switching orgs will show the nudge again even if it was dismissed in another org.
 */
export const ReconnectGANudgeDialog = ({
  hasGAWebProperty,
  hasGAAuthorization,
  organizationId,
}: ReconnectGANudgeDialogProps) => {
  const history = useHistory();

  // Only show for an explicit "disconnected" state. Treat unknown/null auth as "don't show yet".
  const orgNeedsReconnect = hasGAWebProperty && hasGAAuthorization === false;
  const [showReconnectGANudge, setShowReconnectGANudge] = useState(orgNeedsReconnect);

  useEffect(() => {
    // Check if user has previously dismissed the alert.
    const cookieData = getCookie(GA_NUDGE_COOKIE_NAME);
    const shouldShow = orgNeedsReconnect && (!cookieData || isExpired(cookieData, organizationId));
    setShowReconnectGANudge(shouldShow);
  }, [orgNeedsReconnect, organizationId]);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      try {
        // Cookie format: timestamp|orgId
        const [timestamp, orgId] = parts.pop().split(";").shift().split("|");
        return {
          timestamp: parseInt(timestamp),
          orgId,
        };
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const isExpired = (cookieData, orgId) => {
    if (!cookieData) return true;

    const now = new Date().getTime();
    const hoursDiff = (now - cookieData.timestamp) / (1000 * 60 * 60);

    return hoursDiff >= REMIND_ME_LATER_HOURS || cookieData.orgId !== orgId;
  };

  const handleRemindMeLater = () => {
    const expires = new Date();
    expires.setHours(expires.getHours() + REMIND_ME_LATER_HOURS);

    // Store both timestamp and orgId in cookie so we can clear it if the org ID no longer matches.
    const cookieValue = `${new Date().getTime()}|${organizationId}`;
    document.cookie = `${GA_NUDGE_COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/`;
    setShowReconnectGANudge(false);
  };

  // Hard-unmount the dialog when hidden to avoid any lingering modal overlays.
  if (!showReconnectGANudge) {
    return null;
  }

  return (
    <HelpDialog
      open={showReconnectGANudge}
      onClose={() => setShowReconnectGANudge(false)}
      maxWidth="xs"
    >
      <Typography variant="h6" align="center" className="ml-lg mr-lg">
        There is a connection issue with your Google Analytics API
      </Typography>
      <div className="mt-md text-center">
        <Typography variant="body2" color="textSecondary">
          You will need to resolve this issue in order to process deletion requests. Reconnect your
          Google Analytics account on the API Integrations page.
        </Typography>
      </div>
      <div className="mt-lg">
        <Button
          variant="contained"
          color="primary"
          className="w-100"
          onClick={() => {
            history.push(Routes.privacyCenter.integrations);
          }}
        >
          Go to API Integrations
        </Button>
        <Button variant="text" className="w-100 mt-xs" onClick={handleRemindMeLater}>
          Remind me later
        </Button>
      </div>
    </HelpDialog>
  );
};
